<template>
  <div class="nav-bar-component">
    <VNavigationDrawer
      v-model="isOpen"
      class="contrast nav-drawer"
      touchless
      clipped
      :app="isDesktop"
      :permanent="isDesktop"
      :temporary="!isDesktop"
      floating
      absolute
      :miniVariant="isMini && isDesktop"
      :class="{ 'is-mini': isMini && isDesktop }"
      @mouseenter.native="handleMouseEnter"
      @mouseleave.native="handleMouseLeave"
      @transitionend="handleNavSize"
    >
      <div class="scroller flex-col">
        <!-- Active feature list -->
        <VList class="route-list py-0">
          <div class="font-light mx-3 mb-2 mt-2 menu-subtitle">
            <h3>
              <b>
                <Center>
                  {{ $store.getters.currentBucket.bucket.name }}
                </Center>
              </b>
            </h3>
          </div>

          <div class="font-light mx-3 my-1 menu-subtitle">Global Views</div>
          <RouteItem
            v-for="route in routes.bucket"
            :key="route.name"
            :route="route"
            :extra="{ params: { id: $store.state.currentBucketId } }"
            @hover-route="onHoverRoute"
            @leave-route="onLeaveRoute"
          />

          <template>
            <div class="font-light mx-3 mt-3 mb-1 menu-subtitle">App Views</div>
            <VOverflowBtn
              ref="appSelect"
              v-model="appSelected"
              :items="appNames"
              label="Select Application"
              class="app-selector mx-3 mb-1"
              :class="{ 'select-hidden': hasFinishedOpening === false }"
              hideDetails
              editable
              @input="handleSelectInput"
            />
            <RouteItem
              v-for="route in routes.app"
              :key="route.name"
              :route="route"
              :extra="{ query: { app: appRouteParam } }"
              :disabled="!currentFocusedApp"
              @hover-route="onHoverRoute"
              @leave-route="onLeaveRoute"
            />
          </template>
        </VList>

        <VList
          v-if="incentives.plus.length || incentives.enterprise.length"
          class="route-list py-0"
        >
          <hr />
          <!-- PLUS PAGES -->
          <div v-if="incentives.plus.length">
            <div class="font-light mx-3 my-1 menu-subtitle">
              PM2 Plus
              <VBtn small color="primary" @click="openPlusSubscription()">Upgrade</VBtn>
            </div>
            <RouteItem
              v-for="incentive in incentives.plus"
              :key="incentive.route.name"
              :route="incentive.route"
              :incentive="incentive"
              @hover-route="onHoverRoute"
              @leave-route="onLeaveRoute"
            />
          </div>

          <!--ENTERPRISE PAGES -->
          <div v-if="incentives.enterprise.length">
            <div class="font-light mx-3 my-1 menu-subtitle">PM2 Enterprise</div>
            <RouteItem
              v-for="incentive in incentives.enterprise"
              :key="incentive.route.name"
              :route="incentive.route"
              :incentive="incentive"
              @hover-route="onHoverRoute"
              @leave-route="onLeaveRoute"
            />
          </div>
        </VList>
        <div v-if="showPortal" class="base jump-to mx-3 py-3 pr-3 mt-2 mb-2" @click="openPortal">
          <div>
            <KmIcon color="text" name="km-icon-jump-to" />
            <Transition
              name="custom-classes-transition"
              enterClass="fade-start"
              enterActiveClass="fade"
              enterToClass="fade-end"
            >
              <span v-if="hasFinishedOpening === true" class="ml-2"> Jump to... </span>
            </Transition>
          </div>
          <Transition
            name="custom-classes-transition"
            enterClass="fade-start"
            enterActiveClass="fade"
            enterToClass="fade-end"
          >
            <div v-if="hasFinishedOpening === true">
              {{ shortcutText }}
            </div>
          </Transition>
        </div>
        <div class="menu-space-bellow"></div>
      </div>
    </VNavigationDrawer>
    <div class="backdrop" :class="{ show: shouldShowBackdrop }" @click="closeMobile" />
  </div>
</template>

<script>
import * as mut from '@/store/mutations'
import RouteItem from './RouteItem'
import { getMenuRoutes, getIncentives, isRouteUserFacing, getLinkToApp } from '@/utils/routeHelpers'
import { BUCKETS_PAGE } from '@/router'
import { ACTIVATE_PM2 } from '@/store/actions'
import runDelayedMixin from '@/mixins/runDelayedMixin'
import KmIcon from '@/components/KmIcon'

export default {
  name: 'NavBar',
  components: {
    RouteItem,
    KmIcon,
  },
  mixins: [runDelayedMixin],
  props: {
    mobileOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hasFinishedOpening: false,
      isOpen: true,
      isMini: true,
      tooltip: {
        style: { opacity: 0, top: 'auto', left: '310px' },
        abstract: '',
        title: '',
      },
      BUCKETS_PAGE,
    }
  },
  computed: {
    routes() {
      const result = {
        app: [],
        bucket: [],
      }
      for (const route of getMenuRoutes(this.$router)) {
        if (isRouteUserFacing(route)) {
          if (route.meta.requireApp) {
            result.app.push(route)
          } else {
            result.bucket.push(route)
          }
        }
      }
      return result
    },
    isDesktop() {
      return !!this.$vuetify.breakpoint.mdAndUp
    },
    incentives() {
      return getIncentives(this.$router, { skipEnabled: true })
    },
    currentFocusedApp() {
      return this.$store.getters.currentAppName
    },
    appRouteParam() {
      return this.currentFocusedApp || 'empty'
    },
    appNames() {
      return this.$store.getters.select({ appName: false }).sortedAppNames
    },
    appSelected: {
      get() {
        return this.$store.getters.currentAppName
      },
      set(val) {
        this.$router.push(getLinkToApp(val))
      },
    },
    shortcutText() {
      return `${navigator.userAgent.includes('Macintosh') === true ? 'cmd' : 'ctrl'}+k`
    },
    showPortal() {
      return this.$store.getters.currentPlanType === 'enterprise'
    },
    shouldShowBackdrop() {
      return !this.isDesktop && this.isOpen
    },
  },
  watch: {
    isMini(value) {
      if (value === true) this.hasFinishedOpening = false
    },
    mobileOpen(next) {
      if (!this.isDesktop) {
        this.isOpen = next
      }
    },
    isOpen(next) {
      if (!this.isDesktop) {
        this.$emit('update:mobileOpen', next)
      }
    },
    isDesktop(next) {
      if (next) {
        this.isOpen = true
        this.isMini = true
      } else {
        this.isOpen = this.mobileOpen
      }
    },
    $route() {
      if (!this.isDesktop) {
        this.isOpen = false
      }
    },
  },
  methods: {
    closeMobile() {
      if (!this.isDesktop) {
        this.isOpen = false
      }
    },
    scrollSubmenuIntoView(container, shown) {
      if (shown) {
        this.runDelayed(
          'scroll-to-submenu',
          () => {
            container.scrollIntoView({
              behavior: 'smooth',
            })
          },
          400,
        )
      } else {
        this.cancelDelayed('scroll-to-submenu')
      }
    },
    trackingMenuClicked(pageName) {},
    handleMouseEnter() {
      this.runDelayed(
        'update-navbar-state',
        () => {
          this.isMini = false
        },
        200,
      )
    },
    handleMouseLeave() {
      this.runDelayed(
        'update-navbar-state',
        () => {
          if (this.$refs.appSelect && this.$refs.appSelect.isFocused) return
          this.isMini = true
        },
        100,
      )
    },
    handleSelectInput() {
      this.$nextTick(() => {
        if (this.$refs.appSelect) {
          this.$refs.appSelect.blur()
        }
      })
    },
    blur() {
      if (this.$refs.appSelect) {
        this.$refs.appSelect.blur()
      }
    },
    openPlusSubscription() {
      this.$store.dispatch(ACTIVATE_PM2, {
        position: this.position,
      })
    },
    openPortal() {
      this.$store.commit(mut.SET_IS_PORTAL_OPEN, { isOpen: true })
    },
    handleNavSize(e) {
      if (e.propertyName === 'width' && this.isMini === false) this.hasFinishedOpening = true
    },

    updateTooltip(event) {
      if (!event) {
        this.cancelDelayed('show-route-tooltip')
        this.tooltip.style.opacity = 0
        return
      }

      const { route, incentive, element } = event

      const show = () => {
        this.tooltip.style.opacity = 1
        this.tooltip.style.top = element.getBoundingClientRect().top + 'px'
        if (incentive) {
          this.tooltip.title = incentive.title
          this.tooltip.abstract = incentive.abstract
        } else {
          this.tooltip.title = route.meta.title
          this.tooltip.abstract = route.meta.abstract
        }
      }

      if (this.tooltip.style.opacity) {
        show()
      } else {
        this.runDelayed('show-route-tooltip', show, 300)
      }
    },

    onHoverRoute(event) {
      this.runDelayed('update-tooltip', () => this.updateTooltip(event), 1)
    },

    onLeaveRoute() {
      this.runDelayed('update-tooltip', () => this.updateTooltip(), 1)
    },

    onTooltipTransitionEnd() {
      if (!this.tooltip.style.opacity) {
        this.tooltip.style.top = 'auto'
      }
    },
  },
  created() {
    if (!this.isDesktop) {
      this.isOpen = this.mobileOpen
    } else {
      this.isOpen = true
    }
  },
}
</script>

<style lang="postcss" scoped>
.nav-bar-component {
  position: relative;
  width: var(--navBarWidth);
  flex-shrink: 0;
  z-index: 11; /* above header */

  & .menu-space-bellow {
    margin-bottom: 160px;
  }

  & hr {
    margin-top: 10px;
    padding-bottom: 10px;
    border-top: 2px solid var(--base);
  }

  & .backdrop {
    position: fixed;
    opacity: 0;
    transition: opacity 0.3s;
    background-color: black;

    &.show {
      opacity: 0.4;
      top: var(--headerHeight);
      bottom: 0;
      left: 0;
      right: 0;
    }
  }

  & .app-selector {
    width: unset;
    padding: 0;

    /** Fix crappy vuetify default select styles **/
    /**  TODO: Possibly extract this into a 'custom-select' class for use accross the app **/
    & >>> .v-input__control {
      &::before {
        content: none;
      }
    }

    & >>> .v-input__slot {
      background: var(--base) !important;
      &::before {
        content: none;
      }
    }

    & >>> .v-input__append-inner {
      margin: 0;
      padding: 0;
    }

    &.select-hidden >>> .v-select__slot {
      & label,
      & input {
        display: none !important;
      }
    }
  }

  & .jump-to {
    min-height: clamp(36px, 6vw, 48px);
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    align-items: center;

    & .km-icon-component {
      width: clamp(32px, 6vw, 48px);
      height: clamp(18px, 3.5vw, 24px);
    }
  }

  & .fade {
    transition: opacity 100ms;
  }

  & .fade-start {
    opacity: 0;
  }

  & .fade-end {
    opacity: 1;
  }
}

/* On mobile/tablet (below md), the navbar should not reserve space */
@media (max-width: 959px) {
  .nav-bar-component {
    width: 0;
  }
}

.v-navigation-drawer {
  font-family: var(--font-stylized);
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-height: 100% !important;
  max-width: none;
  padding: 0;

  & >>> .child-link {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  & >>> .v-list__tile {
    height: 36px !important;
    font-size: var(--fs-body-1);
    font-weight: 500;
  }

  & .scroller {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &.is-mini {
    & .scroller {
      overflow-y: hidden;
    }

    & >>> .route-list {
      & .child-link {
        transform: translate3d(-56px, 0, 0);
      }

      & .app-selector {
        & label {
          opacity: 0;
        }
      }
    }
  }

  & >>> .action-centered {
    min-width: 48px !important;
    width: 48px !important;
    display: flex;
    justify-content: center;
  }
}

.v-navigation-drawer--mini-variant {
  & >>> .v-divider,
  & >>> .v-list--group {
    display: block !important;
  }

  & >>> .v-subheader {
    display: flex !important;
  }
}

.menu-subtitle {
  color: var(--gray);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

/* Make sure the text container doesn't shrink, so the 'arrow down' icon is pushed outside the
  navbar when it is closed */
.v-list__group__header > div {
  flex-shrink: 0 !important;
}

.tooltip {
  position: fixed;
  opacity: 0;
  transition: opacity 0.2s, top 0.2s;
  background-color: var(--contrast);
  max-width: 400px;
  border-radius: 4px;
  pointer-events: none;
}
</style>
